import React, { useState } from "react";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { STORIES } from "@/data/siteContent";

export default function SuccessStories() {
  const [idx, setIdx] = useState(0);
  const prev = () => setIdx((i) => (i - 1 + STORIES.length) % STORIES.length);
  const next = () => setIdx((i) => (i + 1) % STORIES.length);

  return (
    <section id="stories" data-testid="stories-section" className="relative py-24 md:py-32 bg-white">
      <div className="container-prose">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <p className="eyebrow mb-5">— Success Stories</p>
            <h2 className="font-serif text-navy text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Dreams we've helped <span className="italic">navigate.</span>
            </h2>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              data-testid="stories-prev"
              onClick={prev}
              aria-label="Previous story"
              className="w-12 h-12 rounded-full border border-navy/20 text-navy hover:bg-navy hover:text-white transition-all flex items-center justify-center"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              data-testid="stories-next"
              onClick={next}
              aria-label="Next story"
              className="w-12 h-12 rounded-full border border-navy/20 text-navy hover:bg-navy hover:text-white transition-all flex items-center justify-center"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Desktop grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-6">
          {STORIES.map((s, i) => (
            <StoryCard key={s.name} story={s} delay={i * 0.1} />
          ))}
        </div>

        {/* Mobile carousel */}
        <div className="md:hidden">
          <StoryCard story={STORIES[idx]} />
          <div className="flex items-center justify-between mt-6">
            <div className="flex gap-2">
              {STORIES.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Go to story ${i + 1}`}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-8 bg-gold" : "w-3 bg-navy/20"
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={prev} data-testid="stories-prev-mobile" aria-label="Previous" className="w-10 h-10 rounded-full border border-navy/20 text-navy flex items-center justify-center">
                <ChevronLeft size={18} />
              </button>
              <button onClick={next} data-testid="stories-next-mobile" aria-label="Next" className="w-10 h-10 rounded-full border border-navy/20 text-navy flex items-center justify-center">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StoryCard({ story, delay = 0 }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay }}
      viewport={{ once: true, margin: "-60px" }}
      data-testid={`story-card-${story.name.toLowerCase().replace(/[. ]/g, "-")}`}
      className="group relative bg-[#FAFAF7] border border-navy/10 p-8 md:p-10 rounded-sm hover:border-gold transition-all duration-500 flex flex-col h-full"
    >
      <Quote className="text-gold" size={42} strokeWidth={1} />
      <p className="mt-4 font-serif text-navy text-lg md:text-[1.2rem] leading-relaxed italic">
        "{story.quote}"
      </p>
      <div className="mt-auto pt-8 border-t border-navy/10">
        <div className="flex items-center gap-1 mb-3">
          {Array.from({ length: story.rating }).map((_, i) => (
            <Star key={i} size={14} className="text-gold fill-gold" />
          ))}
        </div>
        <p className="font-serif text-navy text-xl">{story.name}</p>
        <p className="text-navy-deeper/70 text-sm mt-1">{story.profile}</p>
        <p className="text-gold text-xs uppercase tracking-widest mt-2 font-semibold">
          {story.subtext}
        </p>
      </div>
    </motion.article>
  );
}
